"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";

export type PresentationModeConfig = {
  sidebarMode: "keep" | "sidebarless";
  recordingMode: boolean;
};

type PresentationModeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (config: PresentationModeConfig) => void;
};

export function PresentationModeDialog({
  open,
  onOpenChange,
  onConfirm,
}: PresentationModeDialogProps) {
  const [sidebarMode, setSidebarMode] = useState<"keep" | "sidebarless">(
    "keep"
  );
  const [recordingMode, setRecordingMode] = useState(false);

  const handleConfirm = () => {
    onConfirm({ sidebarMode, recordingMode });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to defaults
    setSidebarMode("keep");
    setRecordingMode(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Presentation Mode Settings</DialogTitle>
          <DialogDescription>
            Configure how presentation mode should display your tier list.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          {/* Sidebar Mode Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Sidebar Options</label>
            <RadioGroup
              value={sidebarMode}
              onValueChange={(value) =>
                setSidebarMode(value as "keep" | "sidebarless")
              }
            >
              <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                <RadioGroupItem value="keep" id="keep-sidebar" />

                <label
                  htmlFor="keep-sidebar"
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  Keep Sidebar
                </label>

                <p className="text-sm text-muted-foreground col-start-2">
                  Sidebar remains visible with unassigned items
                </p>

                <RadioGroupItem value="sidebarless" id="sidebarless" />
                <div className="space-y-1">
                  <label
                    htmlFor="sidebarless"
                    className="text-sm font-medium leading-none cursor-pointer"
                  >
                    Sidebarless (Context Menu)
                  </label>
                  <p className="text-sm text-muted-foreground">
                    Hide sidebar, right-click tiers to add items
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>

          {/* Recording Mode Checkbox */}
          <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <Checkbox
              id="recording-mode"
              checked={recordingMode}
              onCheckedChange={(checked) => setRecordingMode(checked === true)}
            />

            <label
              htmlFor="recording-mode"
              className="text-sm font-medium leading-none cursor-pointer"
            >
              Recording Mode
            </label>
            <p className="col-start-2 text-sm text-muted-foreground">
              Add a &quot;Discussing&quot; area and video placeholder for
              content creation
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>Enter Presentation Mode</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
